/* eslint-disable no-console */
/* eslint-disable react/require-default-props */
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthentication } from '../context/AuthenticationProvider';
import Config from '../../app-config';

export interface SignInOidcFragmentProps {
  redirectMessage?: string;
  navigate?: (path: string) => void;
  isPopup?: boolean;
}

export const SignInOidcFragment = ({
  redirectMessage,
  navigate: navigateFn,
  isPopup = false,
}: SignInOidcFragmentProps): ReactElement => {
  const history = useHistory();
  const { t } = useTranslation('common');
  const { signinRedirectCallback, signinPopupCallback } = useAuthentication();
  const nav = useMemo(
    () => navigateFn || ((path: string) => history.replace(path)),
    [history, navigateFn],
  );
  const initialized = useRef(false);
  const assignedRedirectPath = useRef<string | null>(null);
  const [redirectPath, setRedirectPath] = useState<string | null>(null);

  async function signinAsync(): Promise<void> {
    const callbackFunc = isPopup ? signinPopupCallback : signinRedirectCallback;
    if (!callbackFunc) {
      console.error('No signin callback function provided');
      nav('/');
      return;
    }
    const { basename } = Config;

    try {
      const user = await callbackFunc();
      const from = user?.state as Location | undefined;
      if (!from || !from.pathname) {
        setRedirectPath('/');
        return;
      }

      const path =
        basename && from.pathname.startsWith(basename)
          ? from.pathname.substring(basename.length)
          : from.pathname;

      setRedirectPath(path.startsWith('/') ? path : `/${path}`);
    } catch (e) {
      console.error(e);
      setRedirectPath('/');
    }
  }

  useEffect(() => {
    if (!redirectPath) return;
    if (assignedRedirectPath.current === redirectPath) return;

    assignedRedirectPath.current = redirectPath;
    nav(redirectPath);
  }, [nav, redirectPath]);

  useEffect(() => {
    if (initialized.current) return;

    initialized.current = true;
    signinAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized.current]);

  return <h1>{redirectMessage || t('authentication.redirecting', 'Redirecting...')}</h1>;
};

export default SignInOidcFragment;
